import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { rupiah, tanggalID, todayISO, toDateInput } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Search } from "lucide-react";
import Pagination, { usePagination } from "@/components/app/Pagination";
import AkunChip from "@/components/app/AkunChip";

const empty = { tanggal: todayISO(), kategori_id: "", kategori_nama: "", nama_vendor: "", nominal: 0, keterangan: "", pengurang_bruto: false, akun_id: "" };

export default function PengeluaranPribadi() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [kategori, setKategori] = useState([]);
  const [akun, setAkun] = useState([]);
  const [q, setQ] = useState("");

  const load = async () => {
    const [a, k, ak] = await Promise.all([api.get("/pengeluaran"), api.get("/kategori", { params: { tipe: "pengeluaran" } }), api.get("/akun")]);
    setItems(a.data); setKategori(k.data); setAkun(ak.data);
  };
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));
  const openNew = () => { setEditId(null); setForm({ ...empty, tanggal: todayISO() }); setOpen(true); };
  const openEdit = (it) => { setEditId(it.id); setForm({ ...empty, ...it, tanggal: toDateInput(it.tanggal) || todayISO(), akun_id: it.akun_id || "", kategori_id: it.kategori_id || "", keterangan: it.keterangan ?? "" }); setOpen(true); };

  const save = async () => {
    if (!form.tanggal) { toast.error("Tanggal wajib diisi"); return; }
    if (!form.nama_vendor?.trim()) { toast.error("Penerima/keterangan wajib diisi"); return; }
    if (!(Number(form.nominal) > 0)) { toast.error("Nominal wajib diisi (lebih dari 0)"); return; }
    const payload = { ...form, nominal: Number(form.nominal), pengurang_bruto: !!form.pengurang_bruto };
    try {
      if (editId) await api.put(`/pengeluaran/${editId}`, payload);
      else await api.post("/pengeluaran", payload);
      toast.success(editId ? "Data diperbarui" : "Pengeluaran tersimpan");
      setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const delOne = async (id) => {
    if (!confirm("Hapus data ini?")) return;
    await api.delete(`/pengeluaran/${id}`); toast.success("Terhapus"); load();
  };

  const akunMap = Object.fromEntries(akun.map((a) => [a.id, a]));
  const filtered = items.filter((it) => {
    const s = q.trim().toLowerCase();
    return !s || [it.kategori_nama, it.nama_vendor, it.tanggal, it.keterangan, akunMap[it.akun_id]?.nama].filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
  });
  const { page, setPage, totalPages, paged, total, pageSize } = usePagination(filtered, 10);

  return (
    <>
      <PageHeader title="Pengeluaran Pribadi" subtitle="Catat pengeluaran pribadi. Tandai sebagai pengurang bila memakai metode Pembukuan." actions={
        <Button onClick={openNew} className="brand-btn rounded-sm" data-testid="add-pengeluaran-btn"><Plus size={14} className="mr-1" /> Tambah</Button>
      } />
      <div className="p-4 md:p-8 space-y-4">
        <div className="card-flat p-4 relative">
          <Search size={14} className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" placeholder="Cari kategori, akun, penerima, catatan, tanggal..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="pgl-search" />
        </div>
        <div className="card-flat overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs md:text-sm" data-testid="pengeluaran-table">
              <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
                <th className="p-3">Tanggal</th><th className="p-3">Kategori</th><th className="p-3">Akun</th><th className="p-3">Penerima</th>
                <th className="p-3 text-right">Nominal</th><th className="p-3">Pengurang</th><th className="p-3 text-right">Aksi</th>
              </tr></thead>
              <tbody>
                {filtered.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-slate-400">Tidak ada data.</td></tr>}
                {paged.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3 whitespace-nowrap">{tanggalID(p.tanggal)}</td>
                    <td className="p-3">{p.kategori_nama || "-"}</td>
                    <td className="p-3"><AkunChip akun={akunMap[p.akun_id]} testid={`pgl-akun-chip-${p.id}`} /></td>
                    <td className="p-3">{p.nama_vendor}{p.keterangan ? <div className="text-[11px] text-slate-600 mt-0.5 whitespace-pre-line" data-testid={`pgl-catatan-${p.id}`}>{p.keterangan}</div> : null}</td>
                    <td className="p-3 text-right font-mono font-semibold text-rose-700">{rupiah(p.nominal)}</td>
                    <td className="p-3">{p.pengurang_bruto ? <span className="text-[10px] px-2 py-0.5 rounded-sm bg-emerald-50 text-emerald-700">Pengurang</span> : "-"}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => openEdit(p)} className="p-1.5 rounded-sm hover:bg-slate-100" data-testid={`edit-pgl-${p.id}`}><Pencil size={14} /></button>
                        <button onClick={() => delOne(p.id)} className="p-1.5 rounded-sm hover:bg-rose-50" data-testid={`del-pgl-${p.id}`}><Trash2 size={14} className="text-rose-600" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} setPage={setPage} totalPages={totalPages} total={total} pageSize={pageSize} testid="pengeluaran-pagination" />
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="pengeluaran-dialog">
          <DialogHeader><DialogTitle>{editId ? "Ubah" : "Tambah"} Pengeluaran Pribadi</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Tanggal <span className="text-rose-500">*</span></Label><Input type="date" value={form.tanggal} onChange={upd("tanggal")} /></div>
            <div className="space-y-1.5">
              <Label>Kategori</Label>
              <Select value={form.kategori_id} onValueChange={(v) => { const k = kategori.find((x) => x.id === v); setForm((f) => ({ ...f, kategori_id: v, kategori_nama: k?.nama || "" })); }}>
                <SelectTrigger><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                <SelectContent>{kategori.map((k) => <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Dari Akun (opsional)</Label>
              <Select value={form.akun_id} onValueChange={(v) => setForm((f) => ({ ...f, akun_id: v }))}>
                <SelectTrigger data-testid="pgl-akun"><SelectValue placeholder="Pilih akun" /></SelectTrigger>
                <SelectContent>{akun.map((a) => <SelectItem key={a.id} value={a.id}>{a.nama}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Penerima / Keterangan <span className="text-rose-500">*</span></Label><Input value={form.nama_vendor} onChange={upd("nama_vendor")} /></div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Nominal (Rp) <span className="text-rose-500">*</span></Label><CurrencyInput value={form.nominal} onValueChange={upd("nominal")} /></div>
            <div className="col-span-1 md:col-span-2 flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-sm p-3">
              <Checkbox checked={!!form.pengurang_bruto} onCheckedChange={(v) => setForm((f) => ({ ...f, pengurang_bruto: !!v }))} data-testid="pgl-pengurang" />
              <div className="text-xs text-slate-600"><b>Pengurang penghasilan usaha</b> — centang bila biaya ini mengurangi penghasilan neto usaha (hanya berlaku bila metode pajak usaha = <b>Pembukuan</b>).</div>
            </div>
            <div className="col-span-1 md:col-span-2 space-y-1.5"><Label>Catatan</Label><Textarea value={form.keterangan} onChange={upd("keterangan")} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Batal</Button>
            <Button onClick={save} className="brand-btn rounded-sm" data-testid="pengeluaran-save">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
